// Supabase Integration Types

export enum SupabaseStatus {
  ACTIVE_HEALTHY = 'ACTIVE_HEALTHY',
  INACTIVE = 'INACTIVE',
  PAUSED = 'PAUSED',
  RESTORING = 'RESTORING',
  PROVISIONING = 'PROVISIONING',
  FAILED = 'FAILED',
  UNKNOWN = 'UNKNOWN'
}

export enum SupabasePlan {
  FREE = 'free',
  PRO = 'pro',
  TEAM = 'team',
  ENTERPRISE = 'enterprise'
}

export interface SupabaseConfig {
  isActivated: boolean;
  supabaseProjectRef: string;
  supabaseProjectId: string;
  region: string;
  organizationId: string;
  apiKeys: {
    publishable: string; // Client-safe key (replaces legacy 'anon')
    secret: string; // Backend-only key (replaces legacy 'service_role')
  };
  connectionString: string;
  apiUrl: string; // Direct Supabase URL (for Claude Agent MCP tools)
  gatewayUrl?: string; // Gateway URL (for frontend SDK with HMAC signing)
  status: SupabaseStatus;
  plan: SupabasePlan;
  activatedAt: Date;
  pausedAt?: Date;
  lastSyncedAt?: Date;
  errorMessage?: string;
}

// DTOs
export interface ActivateProjectDto {
  region: string;
}

export interface ExecuteQueryDto {
  sql: string;
  params?: any[];
}

export interface CreateFunctionDto {
  name: string;
  body: string;
  verifyJwt?: boolean;
  importMapEnabled?: boolean;
}

export interface UpdateFunctionDto {
  body?: string;
  verifyJwt?: boolean;
  importMapEnabled?: boolean;
}

export interface DeployFunctionDto {
  functionName: string;
  body: string;
}

// Response Types
export interface SupabaseActivationStatusResponse {
  status: SupabaseStatus;
  isActivated: boolean;
  progress?: number;
}

export interface SupabaseConfigResponse {
  apiUrl: string; // Direct Supabase URL (for Claude Agent MCP tools)
  gatewayUrl?: string; // Gateway URL (for frontend SDK with HMAC signing)
  publishableKey: string; // Client-safe key (replaces legacy 'anonKey')
  connectionString: string;
  region?: string;
  status?: SupabaseStatus;
}

export interface SupabaseTableInfo {
  tableName: string;
  schemaName: string;
  rowCount?: number;
}

export interface SupabaseFunctionInfo {
  id: string;
  name: string;
  slug: string;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface SupabaseFunctionStatusResponse {
  workspace: {
    name: string;
    path: string;
  }[];
  deployed: SupabaseFunctionInfo[];
  needsSync: string[];
}

export interface SupabaseUserInfo {
  id: string;
  email?: string;
  phone?: string;
  createdAt: string;
  lastSignInAt?: string;
  emailConfirmedAt?: string;
  phoneConfirmedAt?: string;
}

export interface SupabaseUserListResponse {
  users: SupabaseUserInfo[];
  total: number;
  page: number;
  perPage: number;
}

// Management API Response Types (Internal)
export interface SupabaseManagementProjectResponse {
  id: string;
  ref?: string; // Optional: API v1 may only return 'id' field
  name: string;
  organization_id: string;
  region: string;
  status: string;
  database?: {
    host: string;
    port: number;
    version: string;
    connection_string: string;
  };
  anon_key?: string; // Only available when status is ACTIVE_HEALTHY
  service_role_key?: string; // Only available when status is ACTIVE_HEALTHY
  created_at: string;
}

export interface SupabaseManagementRegionResponse {
  id: string;
  name: string;
  location: string;
}

export interface SupabaseManagementApiError {
  code: string;
  message: string;
  details?: any;
}

// ==================== ADVISOR TYPES ====================

// Lint severity levels from Supabase Management API
export enum SupabaseLintLevel {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
}

// Lint categories
export enum SupabaseLintCategory {
  SECURITY = 'SECURITY',
  PERFORMANCE = 'PERFORMANCE',
}

// Single lint/advisor result from Supabase Management API
export interface SupabaseLint {
  name: string;           // e.g., "unindexed_foreign_keys", "rls_disabled_in_public"
  title: string;          // Human-readable title
  level: SupabaseLintLevel;
  facing: string;         // e.g., "EXTERNAL"
  categories: SupabaseLintCategory[];
  description: string;    // What the issue is
  detail: string;         // Specific details (table/column names)
  remediation: string;    // How to fix it (often includes SQL)
  metadata: {
    schema?: string;
    name?: string;        // table/function name
    type?: string;        // "table", "function", etc.
  };
}

// Advisor API response format
export interface SupabaseAdvisorsResponse {
  lints: SupabaseLint[];
}

// Advisor type parameter
export type SupabaseAdvisorType = 'security' | 'performance';
